// ─────────────────────────────────────────────────────────────────────────────
// Modals.jsx  —  GPS Recall modal + Map Popup modal
//
// GPSModal     — full driver list with live lat/lng. Slides up from bottom
//                on mobile, centered on desktop. "View on Map" opens MapPopupModal.
//
// MapPopupModal — shows a specific driver pinned on the tracking map with
//                 speed, fuel, and coordinate readouts.
//
// WHAT TO EDIT HERE:
//   Driver row layout  → edit inside {DRIVERS.map(...)} in GPSModal
//   Stat columns       → edit the grid-cols-4 in MapPopupModal
//   Modal size         → change sm:max-w-md / sm:max-w-lg
//   Close button style → find the × buttons
// ─────────────────────────────────────────────────────────────────────────────

import { DRIVERS } from "./constants";
import TrackingMap from "./TrackingMap";

// ── GPS Recall Modal ──────────────────────────────────────────────────────────
export function GPSModal({ simIdx, simProg, simRunning, onClose, onViewMap }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{background:"rgba(0,0,0,0.5)"}}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900">GPS Recall</h2>
            <p className="text-xs text-gray-400 mt-0.5">Live positions — all drivers</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Wire this to a real refresh action if needed */}
            <button className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 text-gray-600 font-medium">↻ Refresh</button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 text-xl">×</button>
          </div>
        </div>

        {/* Driver rows — data from DRIVERS in constants.js */}
        <div className="overflow-y-auto divide-y flex-1">
          {DRIVERS.map(d => (
            <div key={d.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-sm flex-shrink-0">{d.avatar}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-800">{d.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    d.status==="Active"   ? "bg-green-100 text-green-700"  :
                    d.status==="On Break" ? "bg-amber-100 text-amber-700"  :
                                           "bg-gray-100 text-gray-500"
                  }`}>{d.status}</span>
                </div>
                {/* Live coordinates */}
                <p className="text-xs text-gray-400 font-mono mt-0.5">{d.lat.toFixed(4)}°N, {Math.abs(d.lng).toFixed(4)}°W</p>
                <p className="text-xs text-gray-300">Last update: {d.lastUpdate}</p>
              </div>
              <button onClick={() => onViewMap(d)} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 font-medium flex-shrink-0">
                View on Map
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex-shrink-0">
          <button onClick={onClose} className="w-full border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Map Popup Modal ───────────────────────────────────────────────────────────
export function MapPopupModal({ driver, simIdx, simProg, simRunning, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{background:"rgba(0,0,0,0.5)"}}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-bold text-gray-900">📍 {driver.name}</h2>
            <p className="text-xs text-gray-400">{driver.vehicle} · {driver.status}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 text-xl">×</button>
        </div>

        {/* Mini map — highlights this driver with a purple pin */}
        <div className="h-48 border-b">
          <TrackingMap simIdx={simIdx} simProg={simProg} simRunning={simRunning} hlDriver={driver}/>
        </div>

        {/* Stats — add more columns here if needed */}
        <div className="px-5 py-4">
          <div className="grid grid-cols-4 gap-3 text-xs mb-4">
            <div>
              <p className="text-gray-400">Lat</p>
              <p className="font-mono font-semibold text-gray-800 mt-0.5">{driver.lat.toFixed(4)}°</p>
            </div>
            <div>
              <p className="text-gray-400">Lng</p>
              <p className="font-mono font-semibold text-gray-800 mt-0.5">{Math.abs(driver.lng).toFixed(4)}°</p>
            </div>
            <div>
              <p className="text-gray-400">Speed</p>
              <p className="font-semibold text-amber-700 mt-0.5">{driver.speed} mph</p>
            </div>
            <div>
              <p className="text-gray-400">Fuel</p>
              <p className="font-semibold text-green-700 mt-0.5">{driver.fuel}%</p>
            </div>
          </div>
          <button onClick={onClose} className="w-full border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50">
            Close Map
          </button>
        </div>
      </div>
    </div>
  );
}
